/**
 * Pure calculation and export payload generation functions for Runbook Bureau micro-tools.
 * Decoupled from React state for exhaustive unit testing and deterministic execution.
 */

export interface RateCardInputs {
  targetNet: number;
  taxRate: number;
  overheadPct: number;
  daysPerMonth: number;
}

export interface RateCardOutputs {
  annualCapacityDays: number;
  taxReserve: number;
  overheadReserve: number;
  totalGrossRequired: number;
  calculatedFloorDayRate: number;
  tier1Price: number;
  tier2Price: number;
  tier3Price: number;
}

/**
 * Calculates the mathematical billable floor rate and 3-tier pricing anchors.
 * Formula: Floor Day Rate = [Target Net * (1 + Tax% + Overhead%)] / (DaysPerMonth * 12)
 */
export function calculateRateCardMetrics(inputs: RateCardInputs): RateCardOutputs {
  const { targetNet, taxRate, overheadPct, daysPerMonth } = inputs;
  const safeDays = Math.max(1, daysPerMonth);
  const annualCapacityDays = safeDays * 12;
  const taxReserve = targetNet * (taxRate / 100);
  const overheadReserve = targetNet * (overheadPct / 100);
  const totalGrossRequired = targetNet + taxReserve + overheadReserve;
  const calculatedFloorDayRate = Math.round(totalGrossRequired / annualCapacityDays);

  // Tier Pricing Anchors
  const tier1Price = Math.round((calculatedFloorDayRate * 2 * 1.15) / 100) * 100; // 2 days + 15% diagnostic premium
  const tier2Price = Math.round((calculatedFloorDayRate * 9) / 250) * 250;       // 2 weeks (9 prod days rounded to $250)
  const tier3Price = Math.round((calculatedFloorDayRate * 4.5) / 100) * 100;     // Retained monthly (4.5 days/mo)

  return {
    annualCapacityDays,
    taxReserve,
    overheadReserve,
    totalGrossRequired,
    calculatedFloorDayRate,
    tier1Price,
    tier2Price,
    tier3Price,
  };
}

export interface RateCardMarkdownParams {
  studioName: string;
  operatorName: string;
  domainRole: string;
  vendorTaxId: string;
  legalJurisdiction: string;
  currency: 'USD' | 'EUR' | 'GBP';
  calculatedFloorDayRate: number;
  tier1Price: number;
  tier2Price: number;
  tier3Price: number;
  isWatermarked?: boolean;
}

export function generateRateCardMarkdown(params: RateCardMarkdownParams): string {
  const {
    studioName,
    operatorName,
    domainRole,
    vendorTaxId,
    legalJurisdiction,
    currency,
    calculatedFloorDayRate,
    tier1Price,
    tier2Price,
    tier3Price,
    isWatermarked = false,
  } = params;

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';

  return `# ${studioName} — 2026 Commercial Schedule of Consulting Fees
**Practice Principal:** ${operatorName} (${domainRole})
**Legal Entity & Tax:** ${studioName} LLC · ${vendorTaxId} (${legalJurisdiction})
**Currency:** ${currency} (ISO 4217)
**Mathematical Billable Floor:** ${currencySymbol}${calculatedFloorDayRate.toLocaleString()}/day
**Document Reference:** REF-2026-RC-482 · Rate Lock Guarantee: 30 Calendar Days

---

### TIER 1: DIAGNOSTIC ARCHITECTURE AUDIT — ${currencySymbol}${tier1Price.toLocaleString()} (48 Hours)
- Full architectural and technical debt audit across databases, state hydration, and edge infrastructure
- 30-min recorded asynchronous video walkthrough with prioritized remediation roadmap
- Guaranteed 48-hour delivery commitment
- *Inclusions:* Static codebase audit, dependency analysis, executive severity matrix, 48h turnaround
- *Exclusions:* Direct code refactoring or deployments, synchronous live calls

### TIER 2: CORE IMPLEMENTATION SPRINT — ${currencySymbol}${tier2Price.toLocaleString()} (2 Weeks)
- Dedicated 10-day execution block (zero concurrent client tasks)
- Complete milestone feature delivery, migration, or refactoring
- Staging deployment + automated test suite + PR handover
- 7-day post-launch warranty support
- *Inclusions:* Full staging cutover, test suite, PR reviews, architectural documentation
- *Exclusions:* Scope additions outside Exhibit A, legacy browser/API support

### TIER 3: RETAINED ADVISORY & PRODUCTION QUEUE — ${currencySymbol}${tier3Price.toLocaleString()}/month (Monthly Retainer)
- 2 reserved execution sprint days per month
- Priority asynchronous technical guidance & code reviews (< 24h SLA)
- Bi-weekly architecture consultation with executive team
- Strict zero-rollover monthly capacity policy

---

### COMMERCIAL TERMS & SETTLEMENT PROTOCOL
- **Milestone Split:** 50% reservation deposit upon execution to lock calendar slot; 50% upon milestone staging cutover.
- **Invoicing Terms:** Payable strictly Net-15. Late payments crossing 5-day grace threshold trigger automatic production freeze.
- **Capacity Exclusivity:** Single-client dedication during active sprint blocks. Zero concurrent client workload.
- **Validity:** Published rates guaranteed for 30 calendar days from issuance.
${isWatermarked ? '\nGenerated with Bureau // Rate Studio' : ''}`;
}

export interface ScopeShieldEmailParams {
  projectName: string;
  clientName: string;
  originalLaunchDate: string;
  revisedLaunchDate: string;
  masterAgreementRef: string;
  addOnPrice: number;
}

export function generateScopeShieldEmail(params: ScopeShieldEmailParams): string {
  const {
    projectName,
    clientName,
    originalLaunchDate,
    revisedLaunchDate,
    masterAgreementRef,
    addOnPrice,
  } = params;

  return `Subject: Scope capture & timeline alignment: [${projectName}]

Hi ${clientName},

Those are solid additions—a merchant dashboard and custom PDF invoicing will definitely enhance the post-launch operations for your finance team.

Because our active sprint is scope-locked to deliver the core Stripe Elements checkout by ${originalLaunchDate}, incorporating a new database reporting dashboard and PDF generation service falls outside our signed Exhibit A deliverables under ${masterAgreementRef}.

To ensure we protect your hard launch deadline without schedule slippage, we have two clean paths forward:

1. OPTION A (RECOMMENDED): We keep our current ${originalLaunchDate} launch date completely locked. I have cataloged the dashboard and PDF features into our Post-Launch Backlog, and will execute them as a dedicated 3-day Add-On Sprint (${addOnPrice ? `$${addOnPrice.toLocaleString()}` : '$2,400'}) starting Monday.
2. OPTION B: If these features are strict blockers for launch, we can pause the current deployment, sign Change Order #01 (attached), and shift our launch target to ${revisedLaunchDate}.

Let me know by 4:00 PM today which option aligns best with your team's launch priorities.

Best regards,
[Your Name]`;
}

export interface ScopeShieldMarkdownParams {
  projectName: string;
  clientName: string;
  masterAgreementRef: string;
  originalLaunchDate: string;
  revisedLaunchDate: string;
  baseScope: string;
  clientMessage: string;
  estimatedDays: number;
  addOnPrice: number;
  isWatermarked?: boolean;
}

export function generateScopeShieldMarkdown(params: ScopeShieldMarkdownParams): string {
  const {
    projectName,
    clientName,
    masterAgreementRef,
    originalLaunchDate,
    revisedLaunchDate,
    baseScope,
    clientMessage,
    estimatedDays,
    addOnPrice,
    isWatermarked = false,
  } = params;

  return `# CHANGE ORDER ADDENDUM #01 // EXHIBIT A ADJUSTMENT
**Document Reference:** REF-2026-CO-0104  
**Master Services Agreement:** ${masterAgreementRef}  
**Date of Issuance:** ${new Date().toLocaleDateString()}  
**Expiration Cutoff:** STRICT 48-HOUR RATIFICATION WINDOW  
**Project:** ${projectName}  
**Client Entity:** ${clientName}  
**Commercial Consideration:** $${addOnPrice.toLocaleString()} USD  
**Timeline Variance:** +${estimatedDays} Calendar Days (Revised Launch: ${revisedLaunchDate})  

---

## 1. Scope Reconciliation & Variance Analysis
- **Original Baseline Scope (Exhibit A):** ${baseScope}
- **Inbound Client Addition:** "${clientMessage}"
- **Impact Assessment:** +${estimatedDays} dedicated engineering production days; +$${addOnPrice.toLocaleString()} USD fixed fee.

---

## 2. Revised Milestone & Delivery Schedule
- **Baseline Delivery Target:** ${originalLaunchDate}
- **Revised Staging Cutover:** ${revisedLaunchDate}
- **Production Variance:** +${estimatedDays} Calendar Days net schedule adjustment

---

## 3. Legal Ratification & Integration Rider
1. **Scope Authorization:** Client authorizes the additive scope items detailed above.
2. **Timeline Shift:** Client explicitly ratifies that the target delivery date shifts to ${revisedLaunchDate}.
3. **Payment Precondition:** Add-on consideration of $${addOnPrice.toLocaleString()} USD shall be invoiced immediately upon execution.
4. **Integration Clause:** Except as expressly modified herein, all covenants of Master Agreement ${masterAgreementRef} remain in full force and effect.

---

**AUTHORIZED CORPORATE EXECUTION:**

CLIENT ENTITY: ${clientName}  
Authorized Signatory: ___________________________  Date: ___________________________  

SERVICE PROVIDER: Systems Architecture Practice  
Authorized Signatory: ___________________________  Date: ${new Date().toLocaleDateString()}  
${isWatermarked ? '\nGenerated with Bureau // Scope Shield · Pro Unlocks Unbranded Vector PDF Export' : ''}`;
}

export interface TeardownLabMarkdownParams {
  targetCompany: string;
  targetUrl: string;
  auditVersion: string;
  targetEnvironment: string;
  samplingWindow: string;
  targetSla: string;
  techStack: string;
  failure1: string;
  failure2: string;
  failure3: string;
  estimatedLoss: number;
  remediationPrice: number;
  isWatermarked?: boolean;
}

export function generateTeardownMarkdown(params: TeardownLabMarkdownParams): string {
  const {
    targetCompany,
    targetUrl,
    auditVersion,
    targetEnvironment,
    samplingWindow,
    targetSla,
    techStack,
    failure1,
    failure2,
    failure3,
    estimatedLoss,
    remediationPrice,
    isWatermarked = false,
  } = params;

  return `# TECHNICAL DUE DILIGENCE MEMORANDUM // ARCHITECTURAL TEARDOWN
**Document Reference:** MEMO-2026-AUDIT-084  
**Revision Index:** ${auditVersion}  
**Classification:** CONFIDENTIAL // BOARD-LEVEL TECHNICAL REVIEW  
**Target Entity:** ${targetCompany} (${targetUrl})  
**Target Environment:** ${targetEnvironment}  
**Observation Window:** ${samplingWindow}  
**Target SLA Benchmark:** ${targetSla}  
**Audited Stack:** ${techStack}  
**Date of Audit:** ${new Date().toLocaleDateString()}  
**Calculated Annual Exposure:** $${(estimatedLoss * 12).toLocaleString()} USD ($${estimatedLoss.toLocaleString()}/mo drag)  

---

## 1. Executive Directive & Reliability Scorecard
A rapid architectural audit and performance profiling was performed on the production stack of ${targetCompany} (${targetUrl}). The system exhibits high-risk failure points in query indexing, unbounded client-side re-renders, and missing edge acceleration. 

- **Reliability Assessment:** GRADE C- (Critical Production Bottlenecks Detected)
- **Monthly Inaction Drag:** $${estimatedLoss.toLocaleString()} USD / month
- **Projected 12-Month Inaction Drag:** $${(estimatedLoss * 12).toLocaleString()} USD / year
- **Remediation Window:** 10 Working Days (Dedicated Implementation Sprint)

---

## 2. Assessment Parameters & Scope Boundaries
- **Assessed Scope:** Core transactional pipeline, API orchestration, and client hydration boundaries.
- **Environment:** ${targetEnvironment}
- **Methodology:** ${samplingWindow} under live peak traffic simulation.
- **Benchmark Target:** ${targetSla}

---

## 3. Severity Definitions Legend
- **CRITICAL:** Immediate data integrity risk, revenue leakage, or core service unavailability (> 2s p95 / complete transaction failure). Immediate triage required.
- **HIGH:** Latency degradation impacting user retention, memory exhaustion, or elevated operational compute drag. Remediate within sprint.
- **MODERATE:** Sub-optimal caching, excess bandwidth/compute egress cost, or unindexed non-critical paths. Optimize during hardening phase.

---

## 4. Identified Vulnerability & Risk Matrix

| Subsystem / Layer | Observed Defect & Failure Point | Severity | Commercial Exposure | 24-Hour Mitigation Directive |
| :--- | :--- | :--- | :--- | :--- |
| **Database Architecture** | ${failure1} | **CRITICAL** | $6,000/mo cart & query abandonment | Add composite indices on foreign keys; analyze query plan |
| **Client State & Hydration** | ${failure2} | **HIGH** | High mobile bounce rate & UI lock | Implement SWR / React Query caching with stale-while-revalidate |
| **Edge Delivery & CDN** | ${failure3} | **MODERATE** | $2,800/mo unoptimized compute egress | Deploy Cloudflare Cache Rules & edge compression |

---

## 5. 3-Phase Implementation Sprint Roadmap (10 Working Days)
- **Phase 1 (Days 1–3) — Urgent Triage & Database Optimization:** Add composite indices on foreign keys, optimize slow execution plans, and tune connection pool parameters.
- **Phase 2 (Days 4–7) — Client Hydration & State Hardening:** Implement SWR / React Query caching with stale-while-revalidate, eliminate duplicate render cycles, and optimize bundle boundaries.
- **Phase 3 (Days 8–10) — Edge Acceleration, Load Testing & Cutover:** Deploy Cloudflare Cache Rules & Brotli compression, run automated load test verification (< 400ms p95), and execute zero-downtime cutover.

---

## 6. Recommended Intervention: Core Implementation Sprint
We recommend deploying an immediate 10-day engineering sprint to neutralize these 3 vulnerabilities prior to scaling traffic:
- **Total Fixed-Fee Investment:** $${remediationPrice.toLocaleString()} USD (Zero Overrun Guarantee)
- **Production Timeline:** 10 Working Days (Single-client dedicated queue)
- **Deliverables:** Shipped staging branch refactor, automated performance regression test suite (< 400ms p95), zero-downtime production cutover, and 7-day post-launch warranty support.

---

## 7. Assessment Scope & Methodology Disclaimer
This memorandum reflects technical observations gathered during an observational due diligence audit. Measurements represent point-in-time sampling of publicly reachable or client-authorized telemetry. Recommendations are designed for immediate risk mitigation and commercial stability.

---

**AUDIT ATTESTATION & SIGN-OFF:**

TARGET ENTITY SPONSOR:  
Name: ___________________________  Title: ___________________________  
Entity: ${targetCompany}  Date: ___________________________  

PRINCIPAL SYSTEMS AUDITOR:  
Name: ___________________________  Title: Systems & Architecture Practice Lead  
Entity: Advisory Practice  Date: ${new Date().toLocaleDateString()}  
${isWatermarked ? '\nGenerated with Bureau // Teardown Lab · Pro Unlocks Unbranded Vector PDF Export' : ''}`;
}

export interface AgreementMarkdownParams {
  contractorName: string;
  contractorTaxId: string;
  clientEntity: string;
  legalJurisdiction: string;
  modelType: 'sprint' | 'retainer';
  feeAmount: number;
  turnaroundHours: number;
  graceDays: number;
  warrantyDays: number;
  isWatermarked?: boolean;
}

export function generateAgreementMarkdown(params: AgreementMarkdownParams): string {
  const {
    contractorName,
    contractorTaxId,
    clientEntity,
    legalJurisdiction,
    modelType,
    feeAmount,
    turnaroundHours,
    graceDays,
    warrantyDays,
    isWatermarked = false,
  } = params;

  return `# MASTER SERVICES AGREEMENT & STATEMENT OF WORK
**Document Reference:** AGMT-2026-MSA-049  
**Effective Date:** ${new Date().toLocaleDateString()}  
**Service Provider:** ${contractorName} (${contractorTaxId})  
**Client Entity:** ${clientEntity}  
**Engagement Structure:** ${modelType === 'sprint' ? 'Dedicated Fixed-Scope Calendar Sprint' : 'Monthly Asynchronous Production Retainer'}  
**Commercial Consideration:** $${feeAmount.toLocaleString()} USD  
**Invoicing Terms:** Net-15 Strict (Automatic Freeze at ${graceDays} Days Past Due)  
**Governing Law:** ${legalJurisdiction}  

---

## PREAMBLE & RECITALS
This Master Services Agreement (the "Agreement") is entered into as of ${new Date().toLocaleDateString()} by and between **${contractorName}** ("Provider") and **${clientEntity}** ("Client").

WHEREAS, Client desires to retain Provider to perform professional systems architecture and software engineering services; and  
WHEREAS, Provider possesses the specialized expertise, production capacity, and technical qualifications to deliver such services;  
NOW, THEREFORE, in consideration of the mutual covenants and commercial consideration set forth herein, the parties agree as follows:

---

### ARTICLE 1: ENGAGEMENT SCOPE & PRODUCTION CAPACITY
${
  modelType === 'sprint'
    ? `The Provider agrees to reserve dedicated engineering production capacity exclusively for Client for a fixed calendar sprint. Deliverables are strictly confined to Exhibit A. Incomplete specifications or delays in Client credential provisioning, third-party API keys, or milestone feedback shall shift delivery dates day-for-day without extending Provider's calendar commitment.`
    : `The Provider agrees to maintain an Asynchronous Task Queue for Client. Standard deliverables shall be processed and submitted for review within ${turnaroundHours} business hours. Client may populate an unlimited backlog of requests, but exactly ONE (1) ticket shall be in active production at any given time.`
}

### ARTICLE 2: EXCLUSION OF SYNCHRONOUS MEETINGS & VELOCITY MANDATE
To maximize engineering velocity, all communication, status reporting, and deliverable reviews shall be conducted asynchronously via the Project Board and recorded video walkthroughs. Live synchronous calls (Zoom/Google Meet) are expressly excluded from base fees and must be booked separately at Provider's prevailing advisory rate ($350/hr).

### ARTICLE 3: INVOICE TERMS & MANDATORY PRODUCTION FREEZE
Payment is due strictly Net-15 from invoice issuance. If an invoice crosses the ${graceDays}-day grace threshold without confirmed settlement, active development, staging deployments, and queue processing automatically pause until full account balance is settled.

### ARTICLE 4: ${modelType === 'sprint' ? 'POST-LAUNCH DEFECT WARRANTY WINDOW' : 'STRICT ZERO-ROLLOVER CAPACITY POLICY'}
${
  modelType === 'sprint'
    ? `The Provider warrants all deliverables under Exhibit A for a period of ${warrantyDays} calendar days post-deployment solely to correct functional defects or regressions. New feature requests, scope changes, or third-party API adjustments during this window require an executed Change Order.`
    : `Retainer capacity represents reserved availability on Provider's production calendar for each 30-day billing cycle. Unused days, hours, or tasks do not accumulate, roll over to subsequent months, or convert into financial credits.`
}

### ARTICLE 5: CONDITIONAL INTELLECTUAL PROPERTY RELEASE
Full title, ownership, and copyright of custom source code produced under this Agreement transfer irrevocably to Client solely upon confirmed receipt of 100% of agreed fees ($${feeAmount.toLocaleString()} USD). Prior to full settlement, Provider retains exclusive equitable title.

### ARTICLE 6: MUTUAL CONFIDENTIALITY & DATA SECURITY
Both parties agree to treat all proprietary source code, credentials, architecture diagrams, customer data, and commercial terms with a strict commercial standard of care. Neither party shall disclose Confidential Information to third parties without prior written consent.

### ARTICLE 7: LIMITATION OF LIABILITY & GOVERNING JURISDICTION
To the maximum extent permitted by applicable law, Provider's aggregate liability arising under this Agreement shall not exceed the total fees actually received by Provider from Client hereunder. This Agreement shall be construed and governed in accordance with the laws of the ${legalJurisdiction}.

### ARTICLE 8: SEVERABILITY, MODIFICATION & ENTIRE AGREEMENT (INTEGRATION RIDER)
This Agreement, together with Exhibit A, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior negotiations, representations, understandings, or agreements, whether written or oral. No amendment, modification, or waiver of any provision of this Agreement shall be effective unless in writing and executed by authorized representatives of both parties. If any provision of this Agreement is held to be invalid or unenforceable, such provision shall be severed and the remaining provisions shall continue in full force and effect.

---

**AGREED & EXECUTED BY AUTHORIZED CORPORATE SIGNATORIES:**

CLIENT ENTITY:  
Entity: ${clientEntity}  
Authorized Signatory: ___________________________  
Printed Name: ___________________________  
Corporate Title: Authorized Corporate Signatory  
Date: ___________________________  

SERVICE PROVIDER:  
Entity: ${contractorName} (${contractorTaxId})  
Authorized Signatory: ___________________________  
Printed Name: ___________________________  
Corporate Title: Principal Systems Architect  
Date: ${new Date().toLocaleDateString()}  
${isWatermarked ? '\nGenerated with Bureau // Agreement Armor · Pro Unlocks Unbranded Vector PDF Export' : ''}`;
}
